(() => {
  if (window.FreeLifeCampaignRuntime) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const pad = (value) => String(value).padStart(2, '0');
  const safeStorage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_error) {
        // Private browsing can disable storage. The component remains usable.
      }
    },
  };

  function parseParts(value) {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;
    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4]),
      minute: Number(match[5]),
      second: Number(match[6] || 0),
    };
  }

  function formatterFor(timeZone) {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    } catch (_error) {
      return null;
    }
  }

  function zonedTimeToUtc(value, timeZone = 'Europe/Berlin') {
    if (!value) return null;
    if (/Z$|[+-]\d{2}:?\d{2}$/.test(value)) {
      const absolute = new Date(value);
      return Number.isNaN(absolute.getTime()) ? null : absolute;
    }

    const wanted = parseParts(value);
    const formatter = formatterFor(timeZone);
    if (!wanted || !formatter) return null;

    let guess = Date.UTC(wanted.year, wanted.month - 1, wanted.day, wanted.hour, wanted.minute, wanted.second);
    for (let iteration = 0; iteration < 3; iteration += 1) {
      const current = Object.fromEntries(
        formatter
          .formatToParts(new Date(guess))
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, Number(part.value)])
      );
      const represented = Date.UTC(current.year, current.month - 1, current.day, current.hour, current.minute, current.second);
      const desired = Date.UTC(wanted.year, wanted.month - 1, wanted.day, wanted.hour, wanted.minute, wanted.second);
      guess += desired - represented;
    }
    return new Date(guess);
  }

  function scheduleState(element, now = new Date()) {
    const preview = window.Shopify?.designMode ? element.dataset.previewState : 'auto';
    if (preview && preview !== 'auto') return preview;
    const start = zonedTimeToUtc(element.dataset.start, element.dataset.timezone);
    const end = zonedTimeToUtc(element.dataset.end, element.dataset.timezone);
    if (start && now < start) return 'upcoming';
    if (end && now >= end) return 'expired';
    return 'active';
  }

  class FreeLifeSchedule extends HTMLElement {
    connectedCallback() {
      if (this.dataset.flDismissible === 'true' && safeStorage.get(this.dataset.flDismissKey)) {
        this.hidden = true;
        return;
      }
      this.update();
      this.timer = window.setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    update() {
      const state = scheduleState(this);
      this.dataset.scheduleState = state;
      const behavior = state === 'upcoming' ? this.dataset.beforeStart || 'hide' : this.dataset.afterEnd || 'hide';
      const visible = state === 'active' || behavior === 'show';
      this.hidden = !visible;
      this.dispatchEvent(new CustomEvent('freelife:schedule-change', { bubbles: true, detail: { state } }));
    }
  }

  class FreeLifeCountdown extends HTMLElement {
    connectedCallback() {
      this.update();
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    update() {
      const now = new Date();
      const start = zonedTimeToUtc(this.dataset.start, this.dataset.timezone);
      const end = zonedTimeToUtc(this.dataset.end, this.dataset.timezone);
      let target = end;
      let state = 'active';

      if (start && now < start) {
        target = start;
        state = 'upcoming';
      } else if (!end || now >= end) {
        state = 'expired';
      }

      const preview = window.Shopify?.designMode ? this.dataset.previewState : 'auto';
      if (preview && preview !== 'auto') state = preview;
      this.dataset.countdownState = state;

      const units = this.querySelector('[data-countdown-units]');
      const message = this.querySelector('[data-countdown-message]');
      const activeLabel = state === 'upcoming' ? this.dataset.upcomingLabel : this.dataset.activeLabel;
      const label = this.querySelector('[data-countdown-label]');
      if (label && activeLabel) label.textContent = activeLabel;

      if (state === 'expired') {
        window.clearInterval(this.timer);
        if (units) units.hidden = true;
        if (message) {
          message.hidden = this.dataset.expiredBehavior === 'hide';
          message.textContent = this.dataset.expiredText || 'Diese Aktion ist beendet.';
        }
        if (this.dataset.expiredBehavior === 'hide') this.hidden = true;
        this.dispatchEvent(new CustomEvent('freelife:countdown-expired', { bubbles: true }));
        return;
      }

      this.hidden = false;
      if (units) units.hidden = false;
      if (message) message.hidden = true;
      let difference;
      if (preview === 'upcoming' || preview === 'active') {
        difference = 3 * 86400000 + 12 * 3600000 + 34 * 60000 + 56 * 1000;
      } else {
        if (!target) return;
        difference = Math.max(0, target.getTime() - now.getTime());
      }
      const days = Math.floor(difference / 86400000);
      difference -= days * 86400000;
      const hours = Math.floor(difference / 3600000);
      difference -= hours * 3600000;
      const minutes = Math.floor(difference / 60000);
      difference -= minutes * 60000;
      const seconds = Math.floor(difference / 1000);

      const values = { days, hours, minutes, seconds };
      Object.entries(values).forEach(([name, value]) => {
        const output = this.querySelector(`[data-countdown-${name}]`);
        if (output) output.textContent = pad(value);
      });
    }
  }

  class FreeLifeSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-fl-slide]'));
      this.previousButton = this.querySelector('[data-fl-previous]');
      this.nextButton = this.querySelector('[data-fl-next]');
      this.pauseButton = this.querySelector('[data-fl-pause]');
      this.dots = Array.from(this.querySelectorAll('[data-fl-dot]'));
      this.progress = this.querySelector('[data-fl-progress]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
      this.paused = false;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true), options);
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true), options);
      this.pauseButton?.addEventListener('click', () => this.togglePause(), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true), options));
      this.addEventListener('mouseenter', () => this.stopAutoplay(), options);
      this.addEventListener('mouseleave', () => this.startAutoplay(), options);
      this.addEventListener('focusin', () => this.stopAutoplay(), options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      }, options);
      this.addEventListener('freelife:schedule-change', () => this.refreshSlides(), options);
      reduceMotion.addEventListener('change', () => this.startAutoplay(), options);
      document.addEventListener('visibilitychange', () => this.startAutoplay(), options);

      this.refreshSlides();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
    }

    availableSlides() {
      return this.slides.filter((slide) => !slide.hidden && !slide.closest('fl-schedule[hidden]'));
    }

    refreshSlides() {
      const available = this.availableSlides();
      if (!available.length) return;
      const current = this.slides[this.index];
      if (!available.includes(current)) this.index = this.slides.indexOf(available[0]);
      this.show(this.index, false);
      const hasMultiple = available.length > 1;
      this.querySelector('[data-fl-controls]')?.toggleAttribute('hidden', !hasMultiple);
    }

    show(requestedIndex, userInitiated) {
      const available = this.availableSlides();
      if (!available.length) return;
      const currentAvailableIndex = Math.max(0, available.indexOf(this.slides[this.index]));
      const requestedSlide = this.slides[requestedIndex];
      let nextSlide = requestedSlide;
      if (!available.includes(requestedSlide)) {
        let requestedAvailableIndex = currentAvailableIndex;
        if (requestedIndex > this.index) requestedAvailableIndex += 1;
        if (requestedIndex < this.index) requestedAvailableIndex -= 1;
        const normalized = ((requestedAvailableIndex % available.length) + available.length) % available.length;
        nextSlide = available[normalized];
      }
      this.index = this.slides.indexOf(nextSlide);

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll(focusableSelector).forEach((item) => {
          if (!active) {
            if (!item.hasAttribute('data-fl-tabindex')) item.dataset.flTabindex = item.getAttribute('tabindex') || '';
            item.setAttribute('tabindex', '-1');
          } else if (item.hasAttribute('data-fl-tabindex')) {
            if (item.dataset.flTabindex) item.setAttribute('tabindex', item.dataset.flTabindex);
            else item.removeAttribute('tabindex');
            delete item.dataset.flTabindex;
          }
        });
        const video = slide.querySelector('video[data-fl-video]');
        if (active && video && video.dataset.autoplay === 'true' && !reduceMotion.matches) video.play().catch(() => {});
        if (!active && video) video.pause();
      });

      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(Number(dot.dataset.index) === this.index)));
      if (this.progress) this.style.setProperty('--fl-progress', `${((available.indexOf(nextSlide) + 1) / available.length) * 100}%`);
      this.dispatchEvent(new CustomEvent('freelife:slide-change', { detail: { index: this.index, userInitiated } }));
      if (userInitiated) this.restartAutoplay();
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      const label = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
      if (label) this.pauseButton.setAttribute('aria-label', label);
      if (this.paused) this.stopAutoplay();
      else this.startAutoplay();
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || this.paused || reduceMotion.matches || document.hidden || this.availableSlides().length < 2) return;
      const interval = Math.max(3000, Number(this.dataset.interval || 6000));
      this.autoplayTimer = window.setInterval(() => this.show(this.index + 1, false), interval);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  class FreeLifeReviewCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fl-review-track]');
      this.cards = Array.from(this.querySelectorAll('[data-fl-review-card]'));
      this.previousButton = this.querySelector('[data-fl-review-previous]');
      this.nextButton = this.querySelector('[data-fl-review-next]');
      this.pauseButton = this.querySelector('[data-fl-review-pause]');
      this.pauseIcon = this.querySelector('[data-fl-review-pause-icon]');
      this.status = this.querySelector('[data-fl-review-status]');
      this.index = 0;
      this.paused = false;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true), options);
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true), options);
      this.pauseButton?.addEventListener('click', () => this.togglePause(), options);
      this.addEventListener('mouseenter', () => this.stopAutoplay(), options);
      this.addEventListener('mouseleave', () => this.startAutoplay(), options);
      this.addEventListener('focusin', () => this.stopAutoplay(), options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      }, options);
      window.addEventListener('resize', () => this.show(Math.min(this.index, this.maxIndex()), false), options);
      reduceMotion.addEventListener('change', () => this.startAutoplay(), options);
      document.addEventListener('visibilitychange', () => this.startAutoplay(), options);

      window.requestAnimationFrame(() => {
        this.show(0, false);
        this.startAutoplay();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
    }

    perView() {
      return window.matchMedia('(min-width: 750px)').matches ? 3 : 1;
    }

    maxIndex() {
      return Math.max(0, this.cards.length - Math.min(this.perView(), this.cards.length));
    }

    show(requestedIndex, userInitiated) {
      if (!this.track || !this.cards.length) return;
      const maximum = this.maxIndex();
      if (requestedIndex > maximum) this.index = 0;
      else if (requestedIndex < 0) this.index = maximum;
      else this.index = requestedIndex;

      const firstOffset = this.cards[0].offsetLeft;
      const targetOffset = this.cards[this.index].offsetLeft - firstOffset;
      this.track.style.transform = `translate3d(${-targetOffset}px, 0, 0)`;

      const visibleCount = Math.min(this.perView(), this.cards.length);
      this.cards.forEach((card, cardIndex) => {
        const visible = cardIndex >= this.index && cardIndex < this.index + visibleCount;
        card.setAttribute('aria-hidden', String(!visible));
      });

      if (this.status) {
        const first = this.index + 1;
        const last = Math.min(this.index + visibleCount, this.cards.length);
        this.status.textContent = `Kundenstimmen ${first} bis ${last} von ${this.cards.length}`;
      }
      if (userInitiated) this.restartAutoplay();
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseButton?.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton?.setAttribute('aria-label', this.paused ? 'Rotation fortsetzen' : 'Rotation pausieren');
      if (this.pauseIcon) this.pauseIcon.textContent = this.paused ? '▶' : 'Ⅱ';
      if (this.paused) this.stopAutoplay();
      else this.startAutoplay();
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        this.dataset.autoplay !== 'true'
        || this.paused
        || reduceMotion.matches
        || document.hidden
        || this.cards.length <= this.perView()
      ) return;
      const interval = Math.max(5000, Number(this.dataset.interval || 7000));
      this.autoplayTimer = window.setInterval(() => this.show(this.index + 1, false), interval);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  class FreeLifeCompass extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.choices = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.choices.forEach((choice, index) => {
        choice.addEventListener('click', () => this.select(index, true), { signal: this.abortController.signal });
        choice.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowDown') nextIndex = (index + 1) % this.choices.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + this.choices.length) % this.choices.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.choices.length - 1;
          this.select(nextIndex, true);
          this.choices[nextIndex].focus();
        }, { signal: this.abortController.signal });
      });
      this.select(Math.max(0, this.choices.findIndex((choice) => choice.getAttribute('aria-selected') === 'true')), false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    select(index, userInitiated) {
      this.choices.forEach((choice, choiceIndex) => {
        const active = choiceIndex === index;
        choice.setAttribute('aria-selected', String(active));
        choice.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
      if (userInitiated) this.dispatchEvent(new CustomEvent('freelife:compass-change', { detail: { index } }));
    }
  }

  class FreeLifePopup extends HTMLElement {
    connectedCallback() {
      this.popup = this.querySelector('[data-fl-popup-dialog]');
      this.closeButtons = Array.from(this.querySelectorAll('[data-fl-popup-close]'));
      this.abortController = new AbortController();
      this.storageKey = `freelife-popup:${this.dataset.popupId}:${this.dataset.version || '1'}`;
      this.lastFocused = null;
      const options = { signal: this.abortController.signal };

      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true), options));
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      document.querySelectorAll(`[data-fl-popup-open="${CSS.escape(this.dataset.popupId)}"]`).forEach((button) => {
        button.addEventListener('click', () => this.open(button), options);
      });
      if (this.querySelector('.form__message[role="status"]')) {
        safeStorage.set(this.storageKey, JSON.stringify({ subscribed: true, timestamp: Date.now() }));
        this.open();
        return;
      }
      if (window.Shopify?.designMode && this.dataset.editorPreview === 'open') {
        this.open();
        return;
      }
      this.configureTrigger();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.triggerTimer);
    }

    eligible() {
      if (window.Shopify?.designMode) return true;
      const stored = safeStorage.get(this.storageKey);
      if (!stored) return true;
      try {
        const record = JSON.parse(stored);
        if (record.subscribed) return false;
        const days = Number(this.dataset.frequencyDays || 14);
        return Date.now() - Number(record.timestamp || 0) > days * 86400000;
      } catch (_error) {
        return true;
      }
    }

    configureTrigger() {
      if (!this.eligible()) return;
      const trigger = this.dataset.trigger || 'manual';
      if (trigger === 'time') {
        this.triggerTimer = window.setTimeout(() => this.open(), Math.max(3, Number(this.dataset.delay || 8)) * 1000);
      }
      if (trigger === 'scroll') {
        const onScroll = () => {
          const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
          const percentage = documentHeight > 0 ? (window.scrollY / documentHeight) * 100 : 0;
          if (percentage >= Number(this.dataset.scrollDepth || 45)) {
            window.removeEventListener('scroll', onScroll);
            this.open();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true, signal: this.abortController.signal });
      }
      if (trigger === 'pageviews') {
        const key = `${this.storageKey}:views`;
        const views = Number(safeStorage.get(key) || 0) + 1;
        safeStorage.set(key, String(views));
        if (views >= Number(this.dataset.pageviews || 2)) this.triggerTimer = window.setTimeout(() => this.open(), 2500);
      }
      if (trigger === 'exit' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.addEventListener('mouseout', (event) => {
          if (event.clientY <= 0 && !event.relatedTarget) this.open();
        }, { signal: this.abortController.signal });
      }
    }

    open(opener = null) {
      if (!this.eligible() && !window.Shopify?.designMode) return;
      this.lastFocused = opener || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('fl-popup-open');
      window.requestAnimationFrame(() => {
        const target = this.querySelector('[data-fl-popup-initial-focus]') || this.querySelector(focusableSelector);
        target?.focus();
      });
    }

    close(persist) {
      this.hidden = true;
      document.documentElement.classList.remove('fl-popup-open');
      if (persist) safeStorage.set(this.storageKey, JSON.stringify({ timestamp: Date.now() }));
      this.lastFocused?.focus?.();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.querySelectorAll(focusableSelector)).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('fl-schedule')) customElements.define('fl-schedule', FreeLifeSchedule);
  if (!customElements.get('fl-countdown')) customElements.define('fl-countdown', FreeLifeCountdown);
  if (!customElements.get('fl-slider')) customElements.define('fl-slider', FreeLifeSlider);
  if (!customElements.get('fl-review-carousel')) customElements.define('fl-review-carousel', FreeLifeReviewCarousel);
  if (!customElements.get('fl-compass')) customElements.define('fl-compass', FreeLifeCompass);
  if (!customElements.get('fl-popup')) customElements.define('fl-popup', FreeLifePopup);

  document.addEventListener('click', async (event) => {
    const dismissButton = event.target.closest('[data-fl-dismiss]');
    if (dismissButton) {
      const target = dismissButton.closest('[data-fl-dismissible="true"]');
      if (target) {
        target.hidden = true;
        safeStorage.set(target.dataset.flDismissKey, String(Date.now()));
      }
      return;
    }
    const button = event.target.closest('[data-fl-copy]');
    if (!button) return;
    const code = button.dataset.flCopy;
    const status = button.parentElement?.querySelector('[data-fl-copy-status]');
    try {
      await navigator.clipboard.writeText(code);
      if (status) status.textContent = button.dataset.successText || 'Code kopiert.';
    } catch (_error) {
      if (status) status.textContent = `Code: ${code}`;
    }
  });

  document.querySelectorAll('[data-fl-dismissible="true"]').forEach((element) => {
    if (safeStorage.get(element.dataset.flDismissKey)) element.hidden = true;
  });

  window.FreeLifeCampaignRuntime = {
    scheduleState,
    zonedTimeToUtc,
    version: '3A.1',
  };
})();
